import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { toast } from "../../components/Toast/toast.ts";
import AuthLayout from "../../components/ui/AuthLayout.tsx";
import AppInput from "../../components/ui/AppInput.tsx";
import AppButton from "../../components/ui/AppButton.tsx";

import { forgotPasswordSchema, ForgotPasswordFormData } from "./forgotPassword.schema.ts";

export default function ForgotPwd() {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    reset,
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: "onChange",
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/cust-portal/forgot-pwd`,
        {
          email: data.email,
        }
      );

      toast.success("Password reset link sent successfully");

      reset();

      navigate("/");
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          "An error occurred. Please try again."
      );
    }
  };

  return (
    <AuthLayout>
      <div className="flex flex-col gap-7">

        {/* TOP BAR */}
        <div className="flex items-center justify-between">

          <button
            type="button"
            onClick={() => navigate("/")}
            className="
              flex items-center gap-2
              text-[12px]
              font-medium
              uppercase
              tracking-[0.18em]
              text-[rgb(var(--text-muted))]
              transition-all duration-200

              hover:text-[rgb(var(--color-gold-2))]
            "
          >
            <ArrowBackRoundedIcon sx={{ fontSize: 18 }} />

            Back
          </button>
        </div>

        {/* HEADER */}
        <div>
          <div className="page-breadcrumb mb-4">
            PASSWORD RECOVERY
          </div>

          <h1 className="page-title">
            Request reset link
          </h1>

          <p className="page-subtitle mt-3 max-w-[420px] leading-7">
            Enter your registered email address and we’ll send
            you a secure password reset link.
          </p>
        </div>

        {/* FORM CARD */}
        <div
          className="
            rounded-[24px]
            border border-[rgb(var(--border-primary))]
            bg-[rgb(var(--surface-1))]
            p-5 sm:p-6
            shadow-[var(--shadow-card)]
          "
        >
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-5"
          >

            <AppInput
              label="Email Address"
              placeholder="Enter your registered email"
              autoComplete="email"
              {...register("email")}
              error={errors.email?.message}
            />

            <AppButton
              type="submit"
              loading={isSubmitting}
              disabled={!isValid || isSubmitting}
              fullWidth
            >
              Request Reset Link
            </AppButton>

          </form>
        </div>

        {/* SUPPORT */}
        <div
          className="
            rounded-2xl
            border border-[rgb(var(--border-primary))]
            bg-[rgba(184,137,58,0.05)]
            px-5
            py-4
          "
        >
          <p className="text-[13px] leading-6 text-[rgb(var(--text-secondary))]">
            Need help accessing your account?
            Contact the support team available 24/7.
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-4">

            <span className="text-[12px] font-medium text-[rgb(var(--text-primary))]">
              (253) 220-7200
            </span>

            <div className="h-1 w-1 rounded-full bg-[rgb(var(--text-muted))]" />

            <span className="text-[12px] font-medium text-[rgb(var(--text-primary))]">
              help@myliveeye.com
            </span>
          </div>
        </div>

      </div>
    </AuthLayout>
  );
}